import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  Container,
  Grid,
  Header,
  Segment,
  Button,
  Icon,
  Label,
  Card,
  Breadcrumb,
  Dimmer,
  Loader,
  Message,
  Divider,
} from 'semantic-ui-react'
import { fetchResourceBySlug } from '../../../lib/datocms'
import './Resources.css'

const ResourceDetail = () => {
  const { slug } = useParams()
  const [resource, setResource] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadResource()
  }, [slug])

  const loadResource = async () => {
    try {
      setLoading(true)
      const data = await fetchResourceBySlug(slug)
      if (!data) {
        setError('Resource not found')
      } else {
        setResource(data)
      }
    } catch (err) {
      console.error('Error loading resource:', err)
      setError('Failed to load resource. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  const getResourceIcon = (type) => {
    switch (type) {
      case 'pdf': return 'file pdf'
      case 'video': return 'video'
      case 'article': return 'newspaper'
      case 'tool': return 'wrench'
      default: return 'file'
    }
  }

  if (loading) {
    return (
      <Dimmer active inverted>
        <Loader size='large'>Loading Resource...</Loader>
      </Dimmer>
    )
  }

  if (error) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
        <Button as={Link} to='/resources' primary>
          <Icon name='arrow left' /> Back to Resources
        </Button>
      </Container>
    )
  }

  if (!resource) {
    return null
  }

  return (
    <div className="resource-detail-page">
      {/* Breadcrumb */}
      <Container style={{ paddingTop: '20px' }}>
        <Breadcrumb>
          <Breadcrumb.Section as={Link} to='/'>Home</Breadcrumb.Section>
          <Breadcrumb.Divider />
          <Breadcrumb.Section as={Link} to='/resources'>Resources</Breadcrumb.Section>
          <Breadcrumb.Divider />
          <Breadcrumb.Section active>{resource.title}</Breadcrumb.Section>
        </Breadcrumb>
      </Container>

      {/* Hero Section */}
      <Segment className="resource-detail-hero" vertical>
        <Container>
          <Grid columns={2} stackable verticalAlign='middle'>
            <Grid.Column width={10}>
              <Label color='blue' ribbon>
                <Icon name={getResourceIcon(resource.type)} />
                {resource.type}
              </Label>
              <Header as='h1'>
                {resource.title}
              </Header>
              <div className="resource-meta">
                {resource.author && (
                  <span>
                    <Icon name='user' />
                    {resource.author}
                  </span>
                )}
                {resource.publishedDate && (
                  <span>
                    <Icon name='calendar' />
                    {resource.publishedDate}
                  </span>
                )}
                {resource.category && (
                  <Label>{resource.category}</Label>
                )}
              </div>
              <p className="resource-description">
                {resource.description}
              </p>
              {resource.tags && resource.tags.length > 0 && (
                <div className="resource-tags">
                  {resource.tags.map((tag, index) => (
                    <Label key={index} size='small'>{tag}</Label>
                  ))}
                </div>
              )}
            </Grid.Column>
            <Grid.Column width={6}>
              {resource.thumbnail?.url ? (
                <img 
                  src={resource.thumbnail.url} 
                  alt={resource.thumbnail.alt || resource.title}
                  style={{ width: '100%', borderRadius: '8px' }}
                />
              ) : (
                <Segment placeholder>
                  <Header icon>
                    <Icon name={getResourceIcon(resource.type)} />
                    {resource.type}
                  </Header>
                </Segment>
              )}
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Action Buttons */}
      <Segment vertical style={{ padding: '40px 0' }}>
        <Container textAlign='center'>
          {resource.downloadUrl && (
            <Button 
              primary 
              size='large' 
              as='a' 
              href={resource.downloadUrl} 
              target='_blank'
            >
              <Icon name='download' />
              Download Resource
            </Button>
          )}
          {resource.videoUrl && (
            <Button 
              primary 
              size='large' 
              as='a' 
              href={resource.videoUrl} 
              target='_blank'
            >
              <Icon name='play' />
              Watch Video
            </Button>
          )}
          <Button basic size='large' as={Link} to='/resources'>
            <Icon name='arrow left' />
            Back to Resources
          </Button>
        </Container>
      </Segment>

      {/* Content Section */}
      {resource.content && (
        <Segment vertical style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <Container>
            <div 
              className="resource-content"
              dangerouslySetInnerHTML={{ __html: resource.content }}
              style={{ fontSize: '1.1em', lineHeight: '1.8' }}
            />
          </Container>
        </Segment>
      )}

      {/* Video Embed */}
      {resource.videoUrl && (
        <Segment vertical style={{ padding: '80px 0' }}>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
              Video Content
            </Header>
            <Segment placeholder style={{ height: '500px' }}>
              <Header icon>
                <Icon name='video play' />
                Video Player
                <Header.Subheader>
                  <a href={resource.videoUrl} target='_blank' rel='noopener noreferrer'>
                    Open video in new tab
                  </a>
                </Header.Subheader>
              </Header>
            </Segment>
          </Container>
        </Segment>
      )}

      {/* Related Resources */}
      {resource.relatedResources && resource.relatedResources.length > 0 && (
        <Segment vertical style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '3em' }}>
              Related Resources
            </Header>
            <Card.Group itemsPerRow={3} stackable>
              {resource.relatedResources.map(related => (
                <Card key={related.id} as={Link} to={`/resources/${related.slug}`}>
                  {related.thumbnail?.url && (
                    <img 
                      src={related.thumbnail.url} 
                      alt={related.thumbnail.alt || related.title}
                      style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                    />
                  )}
                  <Card.Content>
                    <Label corner='right'>
                      <Icon name={getResourceIcon(related.type)} />
                    </Label>
                    <Card.Header>{related.title}</Card.Header>
                    <Card.Description>{related.excerpt}</Card.Description>
                  </Card.Content>
                  <Card.Content extra>
                    <Button primary fluid>
                      View Resource <Icon name='arrow right' />
                    </Button>
                  </Card.Content>
                </Card>
              ))}
            </Card.Group>
          </Container>
        </Segment>
      )}

      {/* Newsletter CTA */}
      <Segment className="resource-newsletter" vertical>
        <Container textAlign='center'>
          <Header as='h2'>Get More Resources Like This</Header>
          <p style={{ fontSize: '1.1em', marginBottom: '2em' }}>
            Subscribe to our newsletter for the latest guides, tips, and industry insights.
          </p>
          <Button primary size='large' as={Link} to='/signup'>
            Subscribe Now
          </Button>
        </Container>
      </Segment>

      {/* CTA Section */}
      <Segment className="resource-detail-cta" inverted vertical>
        <Container textAlign='center'>
          <Header as='h2' inverted>
            Ready to Put This Knowledge Into Action?
          </Header>
          <p style={{ fontSize: '1.1em', marginBottom: '2em' }}>
            Start using Trading Systems today and transform your business operations.
          </p>
          <Button size='large' inverted as={Link} to='/signup'>
            Start Free Trial
          </Button>
          <Button size='large' inverted basic as={Link} to='/contact-us'>
            Talk to an Expert
          </Button>
        </Container>
      </Segment>
    </div>
  )
}

export default ResourceDetail